import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { backendApi } from "@/api/backendApi";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
    MapPin,
    Briefcase,
    IndianRupee,

    Building2,
    Bookmark,
    BookmarkCheck,
    ArrowLeft,
} from "lucide-react";


export default function JobDetails() {
    const { id } = useParams();
    const navigate = useNavigate();

    const [savedJobMap, setSavedJobMap] = useState({});
    const [appliedJobs, setAppliedJobs] = useState(new Set());
    const [actionLoading, setActionLoading] = useState(false);

    // Fetch specific job details
    const { data: job, isLoading, error } = useQuery({
        queryKey: ["job", id],
        queryFn: () => backendApi.entities.Job.get(id),
    });

    // Load User's Saved Jobs & Applications
    useQuery({
        queryKey: ["user-data-details"],
        queryFn: async () => {
            if (!backendApi.auth.getToken()) return null;

            try {
                const [saved, apps] = await Promise.all([
                    backendApi.entities.SavedJob.list(),
                    backendApi.entities.Application.list(),
                ]);

                const sMap = {};
                saved.forEach(
                    (s) => (sMap[s.job?.id || s.job?._id || s.job] = s.id)
                );
                setSavedJobMap(sMap);

                const appSet = new Set(
                    apps.map((a) => a.job?.id || a.job?._id || a.job)
                );
                setAppliedJobs(appSet);

                return { saved, apps };
            } catch (err) {
                console.error("Error loading user data:", err);
                return null;
            }
        },
    });

    const isSaved = !!savedJobMap[id];
    const isApplied = appliedJobs.has(id);

    const handleSaveJob = async () => {
        if (!backendApi.auth.getToken()) {
            alert("Please login to save this job.");
            navigate("/jobs/auth");
            return;
        }

        try {
            if (isSaved) {
                await backendApi.entities.SavedJob.delete(savedJobMap[id]);

                const newMap = { ...savedJobMap };
                delete newMap[id];
                setSavedJobMap(newMap);
            } else {
                const res = await backendApi.entities.SavedJob.create({
                    job: id,
                });

                setSavedJobMap({
                    ...savedJobMap,
                    [id]: res.id,
                });
            }
        } catch (error) {
            alert(error.message || "Failed to toggle save job");
        }
    };

    const handleApply = async () => {
        if (!backendApi.auth.getToken()) {
            alert("Please login to apply.");
            navigate("/jobs/auth");
            return;
        }

        if (isApplied) return;

        setActionLoading(true);

        try {
            await backendApi.entities.Application.create({
                job: id,
            });

            setAppliedJobs(new Set([...appliedJobs, id]));

            alert(
                "Successfully applied! Your resume on file has been sent to the employer."
            );
        } catch (error) {
            alert(error.message || "Failed to apply");
        } finally {
            setActionLoading(false);
        }
    };

    const formatSalary = (min, max) => {
        if (!min && !max) return "Not disclosed";
        if (min && max) return `₹${min} - ${max} LPA`;
        if (min) return `₹${min}+ LPA`;
        return `Up to ₹${max} LPA`;
    };

    const formatExperience = (min, max) => {
        if (!min && !max) return "Any experience";
        if (min && max) return `${min} - ${max} years`;
        if (min) return `${min}+ years`;
        return `Up to ${max} years`;
    };

    if (isLoading) {
        return (
            <div className="max-w-4xl mx-auto p-6 space-y-6 mt-10">
                <Skeleton className="h-12 w-3/4" />
                <Skeleton className="h-8 w-1/2" />
                <Skeleton className="h-32 w-full" />
                <Skeleton className="h-64 w-full" />
            </div>
        );
    }

    if (error || !job) {
        return (
            <div className="flex flex-col items-center justify-center py-20 min-h-screen">
                <h2 className="text-2xl font-bold text-gray-800">
                    Job not found
                </h2>
                <p className="text-gray-500 mt-2">
                    This job posting may have been removed or is no longer
                    available.
                </p>
                <Button
                    onClick={() => navigate("/jobs/list")}
                    className="mt-6 bg-[#2557a7]"
                >
                    Back to Jobs
                </Button>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 pb-20">
            {/* HEADER */}
            <div className="bg-white border-b sticky top-0 z-10 shadow-sm">
                <div className="max-w-5xl mx-auto px-4 py-4 sm:px-6 lg:px-8">
                    <button
                        onClick={() => navigate(-1)}
                        className="flex items-center text-sm text-gray-500 hover:text-[#2557a7] mb-4"
                    >
                        <ArrowLeft className="w-4 h-4 mr-1" />
                        Back to search results
                    </button>

                    <div className="flex flex-col md:flex-row md:items-start justify-between gap-6">
                        <div className="flex items-start gap-5">
                            <div className="w-16 h-16 rounded-xl bg-gray-50 border flex items-center justify-center">
                                {job.company_logo ? (
                                    <img
                                        src={job.company_logo}
                                        alt={job.company}
                                        className="w-12 h-12 object-contain rounded-md"
                                    />
                                ) : (
                                    <Building2 className="w-8 h-8 text-gray-400" />
                                )}
                            </div>

                            <div>
                                <h1 className="text-2xl font-bold text-gray-900">
                                    {job.title}
                                </h1>
                                <p className="text-lg text-[#2557a7] mt-1">
                                    {job.company}
                                </p>

                                <div className="flex flex-wrap items-center gap-4 mt-3 text-sm text-gray-600">
                                    <span className="flex items-center gap-1.5">
                                        <MapPin className="w-4 h-4" />
                                        {job.location}
                                    </span>

                                    <span className="flex items-center gap-1.5">
                                        <Briefcase className="w-4 h-4" />
                                        {formatExperience(
                                            job.experience_min,
                                            job.experience_max
                                        )}
                                    </span>

                                    <span className="flex items-center gap-1.5">
                                        <IndianRupee className="w-4 h-4" />
                                        {formatSalary(job.salary_min, job.salary_max)}
                                    </span>
                                </div>
                            </div>
                        </div>

                        <div className="flex items-center gap-3">
                            <Button
                                variant="outline"
                                onClick={handleSaveJob}
                                className={`flex items-center gap-2 ${isSaved
                                    ? "text-[#2557a7] border-[#2557a7] bg-blue-50"
                                    : ""
                                    }`}
                            >
                                {isSaved ? (
                                    <BookmarkCheck className="w-4 h-4" />
                                ) : (
                                    <Bookmark className="w-4 h-4" />
                                )}
                                {isSaved ? "Saved" : "Save Job"}
                            </Button>

                            <Button
                                onClick={handleApply}
                                disabled={isApplied || actionLoading}
                                className={`px-8 ${isApplied
                                    ? "bg-green-600"
                                    : "bg-[#2557a7] hover:bg-[#183971]"
                                    }`}
                            >
                                {actionLoading
                                    ? "Processing..."
                                    : isApplied
                                        ? "Applied"
                                        : "Apply Now"}
                            </Button>
                        </div>
                    </div>
                </div>
            </div>

            {/* BODY */}
            <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 mt-8 grid grid-cols-1 lg:grid-cols-3 gap-8">

                {/* LEFT */}
                <div className="lg:col-span-2 space-y-6">
                    <div className="bg-white p-6 rounded-2xl border shadow-sm">
                        <h2 className="text-lg font-bold mb-4">
                            Job Description
                        </h2>
                        <div className="text-gray-700 whitespace-pre-line">
                            {job.description ||
                                "No description provided by the employer."}
                        </div>
                    </div>

                    <div className="bg-white p-6 rounded-2xl border shadow-sm">
                        <h2 className="text-lg font-bold mb-4">
                            Key Skills Required
                        </h2>

                        {job.skills?.length > 0 ? (
                            <div className="flex flex-wrap gap-2">
                                {job.skills.map((skill, index) => (
                                    <Badge key={index}>{skill}</Badge>
                                ))}
                            </div>
                        ) : (
                            <p className="text-gray-500">
                                No specific skills listed.
                            </p>
                        )}
                    </div>
                </div>

                {/* RIGHT */}
                <div className="space-y-6">
                    <div className="bg-white p-6 rounded-2xl border shadow-sm">
                        <h3 className="font-bold mb-3">Job Overview</h3>

                        <p className="text-sm text-gray-600">
                            Posted on:{" "}
                            {job.posted_date
                                ? new Date(job.posted_date).toLocaleDateString()
                                : "N/A"}
                        </p>
                    </div>
                </div>

            </div>
        </div>
    );
}